import { Workbook } from "exceljs";
import {
	EventRatingsEnrollment,
	EventRatingsEnrollmentEvent,
	EventRatingsResponse,
} from "../../services/reports.service";
import { applyHeaderStyle } from "./workbook-styles";

/**
 * SIMS-9052 — Excel's own rules for a worksheet name, which exceljs enforces
 * inconsistently and so must be applied here:
 *
 *   - `* ? : \ / [ ]` are rejected — `addWorksheet` THROWS on them.
 *   - a duplicate name is rejected — `addWorksheet` THROWS.
 *   - over 31 characters is NOT rejected: exceljs warns and truncates. That is
 *     the dangerous one. Two names that differ only after character 31 both
 *     pass the duplicate check, then truncate to the same string, and the file
 *     is written with two identically-named sheets — which Excel reports as
 *     corrupt. So the cap is applied before the name reaches exceljs.
 */
const SHEET_NAME_MAX = 31;
const SHEET_NAME_SEPARATOR = " - ";
const ILLEGAL_SHEET_CHARS = /[*?:\\/[\]]/g;

/**
 * Every suffix this module appends. The LONGEST one sets how much room the
 * event name gets — on every sheet, not just on that one.
 *
 * Sizing the room per suffix instead is what the first cut did, and it cropped
 * the same event to a different length on each of its tabs: "Mood Check in -
 * Distress rating" next to "Mood Check - Emoji distribution", and "Sleep check
 * i" next to "Sleep chec". A reader scanning the tab strip could not tell they
 * belonged together, and the shorter cuts read as corrupted text.
 */
const SHEET_SUFFIXES = [
	"Population avg",
	"Distress rating",
	"Emoji distribution",
	"Per enrollment",
] as const;

const EVENT_NAME_MAX =
	SHEET_NAME_MAX -
	Math.max(...SHEET_SUFFIXES.map((s) => s.length)) -
	SHEET_NAME_SEPARATOR.length;

/**
 * The event name cut to `EVENT_NAME_MAX`, at a word boundary where there is
 * one.
 *
 * Cutting mid-word is what turns "Sleep check in" into "Sleep chec", which
 * reads as damage rather than as an abbreviation. Falling back to the last
 * whole word gives "Sleep" — shorter, but recognisable, and the same on all
 * three of that event's tabs.
 *
 * A single word longer than the budget has no boundary to fall back to, so it
 * is cut hard; that is still better than overflowing into Excel's own silent
 * truncation.
 */
const trimEventName = (name: string): string => {
	if (name.length <= EVENT_NAME_MAX) return name;
	const cut = name.slice(0, EVENT_NAME_MAX);
	if (name[EVENT_NAME_MAX] === " ") return cut.trimEnd();
	const lastSpace = cut.lastIndexOf(" ");
	return lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
};

/**
 * `<Event> - <Sheet>`, e.g. "Sleep - Emoji distribution".
 *
 * The export now writes one set of these per rateable event, so the bare names
 * no longer identify a sheet: a program with Sleep and Mood would ask for two
 * "Distress rating" tabs. "Per enrollment" additionally collides with the
 * Incentive Disbursement sheet of the same name, which has been in the
 * workbook since SIMS-5825.
 *
 * The event goes first so a reader scanning the tab strip sees each event's
 * sheets grouped together.
 */
const eventSheetName = (
	workbook: Workbook,
	eventName: string,
	suffix: string,
): string => {
	const cleaned = trimEventName(
		eventName.replace(ILLEGAL_SHEET_CHARS, " ").replace(/\s+/g, " ").trim(),
	);
	const base = cleaned
		? `${cleaned}${SHEET_NAME_SEPARATOR}${suffix}`
		: suffix.slice(0, SHEET_NAME_MAX);

	if (!workbook.getWorksheet(base)) return base;
	// Truncation can make two distinct events collide ("Sleep check-in AM" and
	// "Sleep check-in PM" both cut to "Sleep chec"). Numbering keeps the file
	// valid; the sheets stay distinguishable by their contents.
	for (let n = 2; n <= 99; n += 1) {
		const tag = ` ${n}`;
		const candidate = `${base.slice(0, SHEET_NAME_MAX - tag.length)}${tag}`;
		if (!workbook.getWorksheet(candidate)) return candidate;
	}
	// 98 events sharing one truncated name is not a real program shape; failing
	// loudly beats writing a corrupt workbook.
	throw new Error(`Cannot derive a unique sheet name for "${eventName}"`);
};

export const addEventRatingsSheets = (
	workbook: Workbook,
	data: EventRatingsResponse,
): void => {
	if (!data.chart) return;
	if (data.chart.ratingType === "SCORED_SUM" && data.chart.scoredSum) {
		addScoredSumSheets(workbook, data);
	} else if (
		data.chart.ratingType === "INDEPENDENT_RATINGS" &&
		data.chart.independent
	) {
		addIndependentSheets(workbook, data);
	}
};

const PROGRAM_DAYS = 14;

type Occurrence = EventRatingsEnrollmentEvent;

const occurrenceKey = (day: number, n: number): string => `d${day}_${n}`;

/**
 * The enrollment's occurrences, each day in the order the API sent them
 * (oldest first by plannedFor).
 *
 * Falls back to byDay, one occurrence per day, when the API has no byEvent: a
 * backend from before SIMS-9147 then still exports its values under Dn-1
 * instead of an empty sheet.
 */
const occurrencesByDay = (
	e: EventRatingsEnrollment,
): Map<number, Occurrence[]> => {
	const events: Occurrence[] = e.byEvent ?? e.byDay ?? [];
	const days = new Map<number, Occurrence[]>();
	for (const ev of events) {
		if (ev.day < 1 || ev.day > PROGRAM_DAYS) continue;
		const list = days.get(ev.day) ?? [];
		list.push(ev);
		days.set(ev.day, list);
	}
	return days;
};

/**
 * SIMS-9147 — the per-enrollment sheet: one row per enrollment, one column per
 * occurrence of the event on each program day (D1-1, D1-2, ... D14-n), the
 * layout the PO asked for.
 *
 * It used to have one column per day (SIMS-5825), filled from byDay, which
 * keeps only each day's highest reading. A day with SOWS at +30/+60/+90 lost
 * two of its three readings, which is what BUG-026 reported.
 *
 * Dn-k is the k-th occurrence SCHEDULED on day n. A reading that was never
 * submitted leaves its cell blank and the later ones stay in their columns, so
 * Dn-k means the same occurrence for every participant. Nothing measured is
 * still not measuring zero: a 0 is written as 0.
 *
 * Each day gets as many columns as the busiest enrollment in the export had
 * that day, and never fewer than one, so every program day keeps a column and
 * exports stay comparable with each other.
 *
 * An independent event pairs an emoji and a distress column per occurrence, as
 * this sheet always has. Emoji cells carry the raw value rather than a label,
 * for the same reason the emoji sheet's headers do: the API exposes bare
 * values with no labels, and this app's EmojiBadge and the design mock read the
 * ordering in opposite directions.
 */
const addPerEnrollmentSheet = (
	workbook: Workbook,
	data: EventRatingsResponse,
	scored: boolean,
): void => {
	const sheet = workbook.addWorksheet(
		eventSheetName(workbook, data.chart?.name ?? "", "Per enrollment"),
	);
	const grouped = data.enrollments.map(occurrencesByDay);

	const columnsPerDay = Array.from({ length: PROGRAM_DAYS }, (_, i) =>
		grouped.reduce(
			(most, days) => Math.max(most, days.get(i + 1)?.length ?? 0),
			1,
		),
	);

	const occurrenceColumns = columnsPerDay.flatMap((count, i) =>
		Array.from({ length: count }, (_, k) => {
			const label = `D${i + 1}-${k + 1}`;
			const key = occurrenceKey(i + 1, k + 1);
			return scored
				? [{ header: label, key, width: 8 }]
				: [
						{ header: `${label} emoji`, key: `${key}emoji`, width: 12 },
						{ header: `${label} distress`, key: `${key}distress`, width: 12 },
					];
		}).flat(),
	);

	sheet.columns = [
		{ header: "Enrollment ID", key: "enrollmentId", width: 28 },
		{ header: "Participant", key: "participantName", width: 28 },
		...occurrenceColumns,
		...(scored
			? [
					{ header: "Avg", key: "avgScore", width: 10 },
					{ header: "Min", key: "minScore", width: 10 },
					{ header: "Max", key: "maxScore", width: 10 },
				]
			: []),
	];
	applyHeaderStyle(sheet.getRow(1));

	data.enrollments.forEach((e, i) => {
		const row: Record<string, unknown> = {
			enrollmentId: e.enrollmentId,
			participantName: e.participantName,
		};
		grouped[i].forEach((events, day) => {
			events.forEach((ev, k) => {
				const key = occurrenceKey(day, k + 1);
				if (scored) {
					row[key] = ev.score ?? "";
				} else {
					row[`${key}emoji`] = ev.emoji ?? "";
					row[`${key}distress`] = ev.distress ?? "";
				}
			});
		});
		if (scored) {
			row.avgScore = e.avgScore ?? "";
			row.minScore = e.minScore ?? "";
			row.maxScore = e.maxScore ?? "";
		}
		const added = sheet.addRow(row);
		if (scored && typeof e.avgScore === "number") {
			added.getCell("avgScore").numFmt = "0.0";
		}
	});
};

const addScoredSumSheets = (
	workbook: Workbook,
	data: EventRatingsResponse,
): void => {
	const chart = data.chart;
	if (!chart?.scoredSum) return;

	const popAvg = workbook.addWorksheet(
		eventSheetName(workbook, chart.name, "Population avg"),
	);
	popAvg.columns = [
		{ header: "Day", key: "day", width: 8 },
		{ header: "Avg score", key: "avgScore", width: 12 },
		{ header: "Submissions", key: "submissionCount", width: 14 },
	];
	applyHeaderStyle(popAvg.getRow(1));

	for (const d of chart.scoredSum.avgScoreByDay) {
		const r = popAvg.addRow(d);
		r.getCell("avgScore").numFmt = "0.0";
	}

	addPerEnrollmentSheet(workbook, data, true);
};

const addIndependentSheets = (
	workbook: Workbook,
	data: EventRatingsResponse,
): void => {
	const chart = data.chart;
	if (!chart?.independent) return;
	const ind = chart.independent;

	const distress = workbook.addWorksheet(
		eventSheetName(workbook, chart.name, "Distress rating"),
	);
	distress.columns = [
		{ header: "Day", key: "day", width: 8 },
		{ header: "Avg distress", key: "avgDistress", width: 14 },
		{ header: "Submissions", key: "submissionCount", width: 14 },
	];
	applyHeaderStyle(distress.getRow(1));
	for (const d of ind.avgDistressByDay) {
		const r = distress.addRow(d);
		r.getCell("avgDistress").numFmt = "0.0";
	}

	const emoji = workbook.addWorksheet(
		eventSheetName(workbook, chart.name, "Emoji distribution"),
	);
	// SIMS-5825: counts AND the share each represents. The counts alone cannot be
	// compared across days, because the number of submissions per day varies.
	//
	// The header keeps the raw emoji value rather than a friendly name. The API
	// sends `emojiField.values` as bare values ("1", "2", "3") with no labels,
	// and the two are not safely interchangeable: this app's own EmojiBadge reads
	// the FIRST value as the best outcome (🙂, green), while the design mock reads
	// its first column as the worst ("Low", "Poor"). Inventing the mapping here
	// would print a label contradicting the colour the UI shows for the same
	// value. The `(count)` / `%` suffixes give the spec's structure; the labels
	// need either the event schema to expose them or a product decision.
	emoji.columns = [
		{ header: "Day", key: "day", width: 8 },
		...ind.emojiField.values.map((v) => ({
			header: `${v} (count)`,
			key: `emoji_${v}`,
			width: 14,
		})),
		...ind.emojiField.values.map((v) => ({
			header: `${v} %`,
			key: `emojiPct_${v}`,
			width: 12,
		})),
	];
	applyHeaderStyle(emoji.getRow(1));
	for (const d of ind.emojiCountsByDay) {
		const rowData: Record<string, unknown> = { day: d.day };
		// The day's own total, not the population's — a percentage of anything
		// else would not sum to 100 across the row.
		const dayTotal = ind.emojiField.values.reduce(
			(sum, v) => sum + (d.counts[v] ?? 0),
			0,
		);
		for (const v of ind.emojiField.values) {
			const count = d.counts[v] ?? 0;
			rowData[`emoji_${v}`] = count;
			// A day with no submissions gets an empty cell, not 0% — nothing was
			// measured, which is different from measuring zero.
			rowData[`emojiPct_${v}`] =
				dayTotal > 0 ? Math.round((count / dayTotal) * 1000) / 10 : "";
		}
		const r = emoji.addRow(rowData);
		for (const v of ind.emojiField.values) {
			if (dayTotal > 0) r.getCell(`emojiPct_${v}`).numFmt = "0.0";
		}
	}

	addPerEnrollmentSheet(workbook, data, false);
};
